import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useIntl } from 'react-intl';
import { NotificationManager } from 'Components/Notifications';
import { checkVehicleRemoteCommandStatus } from '../api/checkVehicleRemoteCommandStatus';
import {
	STATUS_AUTO_CHECK_WINDOW_MS,
	STATUS_CHECK_INTERVAL_MS,
	STATUS_MANUAL_CHECK_COOLDOWN_MS,
	isActiveStatus,
} from '../constants';
import { applyRemoteCommandUpdate } from '../remoteCommandsNotifications';

const AUTO_CHECKS = Math.floor(STATUS_AUTO_CHECK_WINDOW_MS / STATUS_CHECK_INTERVAL_MS);

/** Seconds left until `until` (ms epoch), rounded up; 0 once it has passed. */
const secondsLeft = (until, now) => Math.max(0, Math.ceil((until - now) / 1000));

/**
 * Status check of the current command while it is active (no backend poller):
 * - `auto`: every 5 s for the first minute after the command becomes current here;
 * - `manual`: then `checkNow()`, enabled at most every 20 s (`cooldownSeconds` > 0 meanwhile).
 * Each result updates the caches (and toasts a final status once). A failed
 * automatic check is retried on the next tick; a failed manual one toasts.
 */
export const useRemoteCommandStatusCheck = (command) => {
	const client = useQueryClient();
	const { messages } = useIntl();
	const id = command?.id ?? null;
	const active = Boolean(id) && isActiveStatus(command?.status);

	const [phase, setPhase] = useState('idle');
	const [cooldownUntil, setCooldownUntil] = useState(0);
	const [now, setNow] = useState(() => Date.now());
	const [checking, setChecking] = useState(false);
	const inFlight = useRef(false);
	const messagesRef = useRef(messages);
	messagesRef.current = messages;

	const runCheck = useRef(null);
	runCheck.current = async (manual) => {
		if (!id || inFlight.current) return;
		inFlight.current = true;
		setChecking(true);
		try {
			const row = await checkVehicleRemoteCommandStatus(id);
			if (row) applyRemoteCommandUpdate(row, client);
		} catch (exc) {
			if (manual) NotificationManager.error(messagesRef.current['oemDataModal_remote_checkFailed']);
		} finally {
			inFlight.current = false;
			setChecking(false);
		}
	};

	// Automatic phase: restarts for each command that becomes current and active here.
	useEffect(() => {
		if (!active) {
			setPhase('idle');
			return undefined;
		}
		setPhase('auto');
		let checks = 0;
		const timer = setInterval(() => {
			checks += 1;
			runCheck.current(false);
			if (checks >= AUTO_CHECKS) {
				clearInterval(timer);
				setPhase('manual');
			}
		}, STATUS_CHECK_INTERVAL_MS);
		return () => clearInterval(timer);
	}, [id, active]);

	// Countdown of the manual button (ticks only while it is cooling down).
	useEffect(() => {
		if (cooldownUntil <= Date.now()) return undefined;
		const timer = setInterval(() => {
			const current = Date.now();
			setNow(current);
			if (current >= cooldownUntil) clearInterval(timer);
		}, 1000);
		return () => clearInterval(timer);
	}, [cooldownUntil]);

	const cooldownSeconds = secondsLeft(cooldownUntil, now);

	const checkNow = () => {
		if (phase !== 'manual' || cooldownSeconds > 0 || inFlight.current) return;
		const current = Date.now();
		setNow(current);
		setCooldownUntil(current + STATUS_MANUAL_CHECK_COOLDOWN_MS);
		runCheck.current(true);
	};

	return { phase: active ? phase : 'idle', checking, cooldownSeconds, checkNow };
};
